import { GetServerSideProps } from 'next';
import { useState } from 'react';
import Image from 'next/image';

import PageContainer from '@/components/PageContainer';
import { contentfulAPIURL } from '@/clients/contentful';
import { createApolloClient } from '@/clients/apollo';
import { queryFooter, queryNavbar } from '@/clients/queries';
import { FooterQuery, NavbarQuery } from 'types/generated';
import { getRadioSchedules } from '@/clients/RadioUtils';
import { RadioSchedule } from '@/models/RadioTypes';
import RichTextBlock from '@/components/Store/RichTextBlock';
import ImageGallery from '@/components/Store/ImgageGallery';
import { GET_ALL_SUB_IMAGES, ImageAsset } from '@/clients/queries/webstore/Webstore';
import VariantPicker from '@/components/Store/VariantPicker';
import { Product } from '@/models/ProductTypes';
import { Document } from '@contentful/rich-text-types';
import ImagePlaceholder from '@/components/common/ImagePlaceholder';
import SubNavigation from '@/components/Store/SubNavigation';
import { CATEGORY_ID_MAP, CATEGORY_NAME_MAP } from '@/config/constants';
import { Log } from '@/logging/datadog';
import { AnimatePresence, motion } from 'framer-motion';
import { resolveVariant, variationRedirectPath } from '@/utils/productVariant';

// Create reverse mapping: value -> key
const REVERSE_CATEGORY_MAP = Object.fromEntries(
  Object.entries(CATEGORY_ID_MAP).map(([key, value]) => [value, key])
) as Record<string, string>;

/* --------------------------------- types ----------------------------------- */
type Props = {
  product: Product | null;
  productDescription?: Document | null | undefined;
  skuImageMap: Record<string, ImageAsset[]>;
  navbar: NavbarQuery;
  energySticker: string | null;
  energyImage: string | null;
  energyInfoLink: string | null;
  footer: FooterQuery;
  radioSchedules: Record<string, RadioSchedule[]>;
  canonicalHref: string;
  initialSku: string;
  variantLabel: string | null;
};

/* ---------------------------------- page ----------------------------------- */
export default function ProductPage({
  product,
  productDescription,
  skuImageMap,
  energySticker,
  energyImage,
  energyInfoLink,
  navbar,
  footer,
  radioSchedules,
  canonicalHref,
  initialSku,
  variantLabel,
}: Props) {
  const [activeSku, setActiveSku] = useState<string>(initialSku);
  const [isEnergyModalOpen, setIsEnergyModalOpen] = useState(false);
  const [activePrice, setActivePrice] = useState<number | undefined>(product?.productPrice);
  const [activeDiscountPrice, setActiveDiscountPrice] = useState<number | undefined>(product?.productDiscountPrice);
  const imagesForActive = skuImageMap[activeSku] ?? [];

  // Calculate discount for currently selected variant only (not a range)
  const calculateSingleDiscount = (price: number | undefined, discountPrice: number | undefined): number | null => {
    if (!discountPrice || !price || discountPrice <= price) return null;
    return Math.floor(((discountPrice - price) / discountPrice) * 100);
  };

  const currentDiscount = calculateSingleDiscount(activePrice, activeDiscountPrice);

  // Share meta is server-rendered, so it reflects the variant in the URL (initialSku), not later client picks.
  const shareName = product?.productName ? [product.productName, variantLabel].filter(Boolean).join(' ') : null;
  const shareTitle = shareName ? `${shareName} | Sýn` : 'Sýn Vefverslun';
  const shareDescription =
    product?.productDescription?.trim() || (shareName ? `${shareName} í vefverslun Sýnar.` : 'Vefverslun Sýnar.');
  const firstImageUrl =
    skuImageMap[initialSku]?.find((img) => img?.url)?.url ||
    Object.values(skuImageMap)
      .flat()
      .find((img) => img?.url)?.url;
  const origin = canonicalHref.replace(/\/vefverslun\/um-voru\/.*$/, '');
  const shareImageUrl = firstImageUrl || `${origin}/android-chrome-192x192.png`;

  return (
    <PageContainer
      title={shareTitle}
      metaTags={{
        title: shareTitle,
        description: shareDescription,
        url: canonicalHref,
        keywords: 'syn,vefverslun,vara',
        image: { url: shareImageUrl },
      }}
      navbar={navbar}
      footer={footer}
      radioSchedules={radioSchedules}
    >
      <div
        className="col-span-full bg-white-100"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            marginBottom: '50px',
            marginTop: '128px',
          }}
          className="mx-auto mt-16 w-full max-w-6xl px-4 text-start"
        >
          {product && (
            <div className="gap-6 mx-24 flex flex-col justify-between  gap-24 md:flex-row">
              <SubNavigation
                category={
                  (REVERSE_CATEGORY_MAP[product.productCategory] as keyof typeof CATEGORY_NAME_MAP) ||
                  product.productCategory ||
                  ''
                }
                product={product}
              />
            </div>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            marginBottom: '50px',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          className="mx-auto mt-16 w-full max-w-6xl px-4"
        >
          <div className="mt-4 flex w-full flex-col sm:mt-0">
            {!product ? (
              <div className="mt-6 p-6 w-full max-w-3xl rounded border text-center">
                <h2 className="text-2xl font-bold">Fann ekki vöru</h2>
                <p className="text-neutral-600">Varan sem þú leitaðir að er ekki til.</p>
              </div>
            ) : (
              <div className="mt-6 w-full">
                <div className="gap-6 mx-24 flex flex-col justify-between  gap-24 md:flex-row">
                  <div className="p-3 relative flex w-full justify-center md:w-1/2">
                    {imagesForActive.length > 0 ? (
                      <ImageGallery
                        images={imagesForActive}
                        discountBadge={
                          currentDiscount !== null && currentDiscount > 0 ? (
                            <div className="absolute bottom-2 right-2 z-10 flex h-100 w-100 flex-col items-center justify-center rounded-full bg-primary-sea text-[14px] tracking-[0.1px] text-white-100 md:hidden">
                              <span className="text-lg font-bold leading-tight">{currentDiscount}%</span>
                              <span className="text-sm font-light leading-tight">Afsláttur</span>
                            </div>
                          ) : undefined
                        }
                      />
                    ) : (
                      <div className="aspect-square max-h-[320px] w-full max-w-[320px]">
                        <ImagePlaceholder bg="bg-white-5" />
                      </div>
                    )}
                    {energySticker && energySticker !== 'none' && (
                      <button
                        type="button"
                        onClick={() => {
                          if (energyImage) {
                            setIsEnergyModalOpen(true);
                          }
                        }}
                        className="h-60 w-60 absolute right-0 top-0 z-10 cursor-pointer"
                        aria-label="Sýna orkumerkingu"
                      >
                        <Image
                          src={`/energyStickers/${energySticker}.png`}
                          alt="Orkumerking"
                          width={60}
                          height={60}
                          className="h-auto w-auto"
                          draggable={false}
                        />
                      </button>
                    )}
                  </div>
                  <div className="w-full rounded p-4 md:w-1/2">
                    <h2 className="mb-2 text-4xl font-bold md:text-3xl">{product?.productName ?? 'Vara'}</h2>
                    {energyInfoLink && (
                      <a
                        href={energyInfoLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mb-2 inline-block text-sm text-red-100 underline hover:no-underline"
                      >
                        Vöruupplýsingablað
                      </a>
                    )}
                    <p className="mb-48 text-sm text-black-50">Vörunr. {activeSku}</p>
                    <VariantPicker
                      product={product}
                      initialSku={initialSku}
                      onSkuChange={setActiveSku}
                      skuImageMap={skuImageMap}
                      onPriceChange={(price, discountPrice) => {
                        setActivePrice(price);
                        setActiveDiscountPrice(discountPrice ?? undefined);
                      }}
                    />
                    {productDescription && (
                      <>
                        <h4 className="mt-2 text-xl font-bold">Helstu upplýsingar</h4>
                        <div className="prose max-w-none">
                          <RichTextBlock content={productDescription} />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <AnimatePresence>
        {isEnergyModalOpen && energyImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="bg-black/60 fixed inset-0 z-50 flex items-center justify-center bg-black-50 bg-opacity-50 p-24"
            onClick={() => setIsEnergyModalOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 50 }}
              transition={{ duration: 0.2 }}
              className="p-6 relative w-fit max-w-[95vw] rounded-2xl bg-[white] shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setIsEnergyModalOpen(false)}
                className="px-3 hover:bg-black/5 absolute right-16 top-16 z-10 rounded-full py-2 text-sm font-semibold text-black-75"
                aria-label="Loka"
              >
                ✕
              </button>

              <div className="relative mx-auto w-fit max-w-full">
                <Image
                  src={energyImage}
                  alt="Orkumerking nánar"
                  width={800}
                  height={1200}
                  className="h-auto max-h-[60vh] w-auto max-w-full object-contain p-32"
                  sizes="(max-width: 768px) 90vw, 720px"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </PageContainer>
  );
}

export const getServerSideProps: GetServerSideProps<Props> = async ({ params, req, query }) => {
  const productid = String(params?.productid || '');
  const requestedSku = typeof query.sku === 'string' ? query.sku : '';

  try {
    const origin =
      process.env.NEXT_PUBLIC_BASE_URL ||
      (process.env.VERCEL_URL ? `https://${process.env.VERCEL_URL}` : `http://${req.headers.host}`);

    const apiURL = `${origin}/api/store/product?slug=${encodeURIComponent(productid)}`;
    const productRes = await fetch(apiURL);
    if (productRes.status === 404) {
      return {
        redirect: {
          destination: '/404',
          permanent: false,
        },
      };
    }
    if (productRes.status >= 400) {
      return {
        redirect: {
          destination: '/500',
          permanent: false,
        },
      };
    }

    const product: Product | null = await productRes.json();

    // A variation SKU used as the slug resolves to its parent; send it to the canonical URL with ?sku=.
    const variationRedirect = variationRedirectPath(product, productid, requestedSku);
    if (variationRedirect) {
      return { redirect: { destination: variationRedirect, permanent: false } };
    }
    let productDescription: Document | null = null;
    let energySticker: string | null = 'none';
    let energyImage: string | null = null;
    let energyInfoLink: string | null = null;

    const skuFromApi = product?.productId;
    const skuImageMap: Record<string, ImageAsset[]> = {};

    const apiUrl = contentfulAPIURL(
      process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID || '',
      process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT || ''
    );
    const apolloClient = createApolloClient(apiUrl, process.env.NEXT_PUBLIC_CONTENTFUL_CD_TOKEN || '');

    if (skuFromApi) {
      const { data } = await apolloClient.query({
        query: GET_ALL_SUB_IMAGES,
        variables: { id: skuFromApi, limitItems: 100, limitImages: 12 },
        fetchPolicy: 'no-cache',
      });
      const parent = data?.productCardStoreCollection?.items?.[0];

      if (!parent) {
        return {
          redirect: {
            destination: '/vefverslun',
            permanent: false,
          },
        };
      }

      energySticker = parent?.energySticker || 'none';
      energyImage = parent?.energyImage?.url || null;
      energyInfoLink = parent?.energyInfoLink || null;
      if (parent?.description) {
        productDescription = parent.description.json;
      }

      const subs = parent?.itemsCollection?.items ?? [];

      for (const it of subs) {
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        const subId = (it as any)?.id as string | undefined;
        if (!subId) continue;
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        const imgs = (it as any)?.imagesCollection?.items ?? [];
        const images: ImageAsset[] = imgs
          // eslint-disable-next-line  @typescript-eslint/no-explicit-any
          .map((node: any) => ({ url: node?.image?.url, title: node?.image?.title }))
          .filter((a: ImageAsset) => a.url);
        skuImageMap[subId] = images;
      }

      const hasAnyImages = Object.values(skuImageMap).some((imgs) => imgs.length > 0);
      if (!hasAnyImages) {
        return {
          redirect: {
            destination: '/vefverslun',
            permanent: false,
          },
        };
      }
    } else {
      return {
        redirect: {
          destination: '/vefverslun',
          permanent: false,
        },
      };
    }

    const [footer, navbar, radioSchedules] = await Promise.all([
      queryFooter(apolloClient),
      queryNavbar(apolloClient),
      getRadioSchedules(),
    ]);

    const { sku: initialSku, label: variantLabel } = resolveVariant(product, requestedSku);
    const skuQuery = initialSku && initialSku !== product?.productId ? `?sku=${encodeURIComponent(initialSku)}` : '';
    const canonicalHref = `${origin}/vefverslun/um-voru/${encodeURIComponent(productid)}${skuQuery}`;

    return {
      props: {
        product,
        productDescription,
        skuImageMap,
        energySticker,
        energyImage,
        energyInfoLink,
        navbar: navbar.data,
        footer: footer.data,
        radioSchedules,
        canonicalHref,
        initialSku,
        variantLabel,
      },
    };
  } catch (err) {
    Log.error((err as Error).message || '', err);

    try {
      const apiUrl = contentfulAPIURL(
        process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID || '',
        process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT || ''
      );
      const apolloClient = createApolloClient(apiUrl, process.env.NEXT_PUBLIC_CONTENTFUL_CD_TOKEN || '');

      const [footer, navbar, radioSchedules] = await Promise.all([
        queryFooter(apolloClient),
        queryNavbar(apolloClient),
        getRadioSchedules(),
      ]);

      const origin =
        process.env.NEXT_PUBLIC_BASE_URL ||
        (process.env.VERCEL_URL ? `https://${process.env.VERCEL_URL}` : `http://${req.headers.host}`);

      return {
        props: {
          product: null,
          productDescription: undefined,
          skuImageMap: {},
          energySticker: null,
          energyImage: null,
          energyInfoLink: null,
          navbar: navbar.data,
          footer: footer.data,
          radioSchedules,
          canonicalHref: `${origin}/vefverslun/um-voru/${encodeURIComponent(String(params?.productid || ''))}`,
          initialSku: '',
          variantLabel: null,
        },
      };
    } catch {
      throw err;
    }
  }
};
